import { createStore } from '@xstate/store';
import type { ResourceDefinition } from '../types';

const RESOURCE_LOCAL_STORAGE_KEY = 'roadmap_2026_resources';
const PERSISTENCE_DEBOUNCE_MS = 500;

export interface ResourceContext {
  resources: ResourceDefinition[];
}

function loadResourcesFromLocalStorage(): ResourceDefinition[] {
  try {
    const saved = localStorage.getItem(RESOURCE_LOCAL_STORAGE_KEY);
    if (!saved) return [];
    const parsed = JSON.parse(saved);
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function saveResourcesToLocalStorage(resources: ResourceDefinition[]): void {
  localStorage.setItem(RESOURCE_LOCAL_STORAGE_KEY, JSON.stringify(resources));
}

const initialContext: ResourceContext = {
  resources: loadResourcesFromLocalStorage(),
};

export const resourceStore = createStore({
  context: initialContext,

  on: {
    'resource.add': (ctx, event: { resource: ResourceDefinition }) => {
      if (ctx.resources.some(r => r.id === event.resource.id)) {
        console.warn(`[resourceStore] Resource with ID "${event.resource.id}" already exists`);
        return ctx;
      }
      return { ...ctx, resources: [...ctx.resources, event.resource] };
    },

    'resource.update': (ctx, event: { resourceId: string; updates: Partial<Omit<ResourceDefinition, 'id' | 'createdAt'>> }) => {
      const idx = ctx.resources.findIndex(r => r.id === event.resourceId);
      if (idx === -1) {
        console.warn(`[resourceStore] Resource with ID "${event.resourceId}" not found`);
        return ctx;
      }
      const updated = [...ctx.resources];
      updated[idx] = { ...updated[idx], ...event.updates, updatedAt: new Date().toISOString() };
      return { ...ctx, resources: updated };
    },

    'resource.delete': (ctx, event: { resourceId: string }) => {
      const idx = ctx.resources.findIndex(r => r.id === event.resourceId);
      if (idx === -1) {
        console.warn(`[resourceStore] Resource with ID "${event.resourceId}" not found`);
        return ctx;
      }
      return { ...ctx, resources: ctx.resources.filter(r => r.id !== event.resourceId) };
    },

    'resources.reset': () => ({ resources: [] }),

    'resources.import': (_ctx, event: { resources: ResourceDefinition[] }) => ({
      resources: event.resources,
    }),
  },
});

// Debounced Persistence
let saveTimeout: ReturnType<typeof setTimeout> | null = null;
let lastResourcesHash = '';

function computeResourcesHash(resources: ResourceDefinition[]): string {
  return resources.map(r =>
    `${r.id}:${r.name}:${r.category}:${r.scope.type}:${r.fields.length}:${r.updatedAt}`
  ).join('|');
}

resourceStore.subscribe((snapshot) => {
  const currentHash = computeResourcesHash(snapshot.context.resources);
  if (currentHash === lastResourcesHash) return;
  lastResourcesHash = currentHash;

  if (saveTimeout) clearTimeout(saveTimeout);
  saveTimeout = setTimeout(() => {
    saveResourcesToLocalStorage(snapshot.context.resources);
  }, PERSISTENCE_DEBOUNCE_MS);
});

// Helper Functions
export function getResourcesArray(): ResourceDefinition[] {
  return resourceStore.getSnapshot().context.resources;
}

export function getResourceById(id: string): ResourceDefinition | undefined {
  return resourceStore.getSnapshot().context.resources.find(r => r.id === id);
}

export function getResourcesByScope(scope: ResourceDefinition['scope']): ResourceDefinition[] {
  const resources = getResourcesArray();
  return resources.filter(r => {
    if (scope.type === 'global') return r.scope.type === 'global';
    if (scope.type === 'board') return r.scope.type === 'board' && r.scope.boardId === scope.boardId;
    if (scope.type === 'canvas') return r.scope.type === 'canvas' && r.scope.boardId === scope.boardId && r.scope.treeId === scope.treeId;
    return false;
  });
}

export function getResourcesByBoardId(boardId: string): ResourceDefinition[] {
  const resources = getResourcesArray();
  return resources.filter(r =>
    r.scope.type === 'global' ||
    (r.scope.type === 'board' && r.scope.boardId === boardId) ||
    (r.scope.type === 'canvas' && r.scope.boardId === boardId)
  );
}

export function getResourcesByTreeId(boardId: string, treeId: string): ResourceDefinition[] {
  const resources = getResourcesArray();
  return resources.filter(r =>
    r.scope.type === 'global' ||
    (r.scope.type === 'board' && r.scope.boardId === boardId) ||
    (r.scope.type === 'canvas' && r.scope.boardId === boardId && r.scope.treeId === treeId)
  );
}

export function generateResourceId(name: string): string {
  const baseId = name
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '_')
    .replace(/^_+|_+$/g, '');

  const existingResources = getResourcesArray();
  let id = `res-${baseId || 'new'}`;
  let counter = 1;

  while (existingResources.some(r => r.id === id)) {
    id = `res-${baseId || 'new'}_${counter}`;
    counter++;
  }

  return id;
}

export type ResourceStore = typeof resourceStore;
